import clsx from 'clsx'
import {
  Copy,
  Search,
  Slack,
  SquareArrowOutUpRight,
  TriangleAlert,
} from 'lucide-react'
import React, { createContext, useContext, useEffect, useState } from 'react'
import { ThreeDots } from 'react-loading-icons'
import { toast } from 'sonner'
import { useDebounceValue, useLocalStorage } from 'usehooks-ts'

import {
  adminEmployees,
  adminEmployerInvoices,
  adminEmployers,
  adminMembers,
  adminPartners,
  adminProspects,
  adminUsers,
  brokersEmployees,
  brokersMembers
} from 'rails_routes'

import {
  type ModelTypes,
  type ModelTypesState,
  type SearchResult,
  useThatchSearch,
} from './hooks/useThatchSearch'
import CopyButtonWithAnimation from './reusable/CopyButtonWithAnimation'
import Tooltip from './reusable/Tooltip'
import { Button } from './ui/button'
import { Checkbox } from './ui/checkbox'
import { Input } from './ui/input'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from './ui/sheet'

type SearchResultHistory = {
  id: string
  identifier?: string
  createdAt: Date
}

type SearchHistory = Record<string, SearchResultHistory>

function sortByCreatedAt (a: SearchResultHistory, b: SearchResultHistory) {
  return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
}

type SearchContextType = {
  setQuery: React.Dispatch<React.SetStateAction<string>>
  setIsHistoryOpen: React.Dispatch<React.SetStateAction<boolean>>
}

function modelTypeFromType (type: string): ModelTypes | undefined {
  switch (type) {
    case 'user':
      return 'User'
    case 'employer':
      return 'Employer'
    case 'employee':
      return 'Employee'
    case 'member':
      return 'Member'
    case 'employerAdmin':
      return 'Employer Admin'
    case 'partner':
      return 'Partner'
    case 'partnerAdmin':
      return 'Partner Admin'
    case 'prospect':
      return 'Prospect'
    default:
      return undefined
  }
}

const SearchContext = createContext<SearchContextType>({
  setQuery: () => {},
  setIsHistoryOpen: () => {},
})

const SelectAllTooltip = ({ children }: { children?: React.ReactNode }) => {
  return (
    <Tooltip triggerChildren={children}>
      Selecting all will search all types of models listed here. Use sparingly
      as these searches are slow and expensive.
    </Tooltip>
  )
}

function ThatchSearchImpl () {
  const [query, setQuery] = useState(() => {
    // Initialize state from the URL.
    const params = new URLSearchParams(window.location.search)
    return params.get('query') || '' // Default to an empty string if not set.
  })
  // Debounce the query to a request is only sent after the user stops typing for 300ms.
  const [queryDebounced] = useDebounceValue(query, 300)

  // Keep track of the types of models to search for.
  const [modelTypes, setModelTypes] = useState<ModelTypesState>(() => {
    const params = new URLSearchParams(window.location.search)

    return {
      user: params.get('user') === 'true' || false,
      employer: params.get('employer') === 'true' || false,
      employee: params.get('employee') === 'true' || false,
      member: params.get('member') === 'true' || false,
      employerAdmin: params.get('employerAdmin') === 'true' || false,
      partner: params.get('partner') === 'true' || false,
      partnerAdmin: params.get('partnerAdmin') === 'true' || false,
      prospect: params.get('prospect') === 'true' || false,
    }
  })
  const { isLoading, data, error } = useThatchSearch({
    query: queryDebounced,
    modelTypes,
  })

  const [isHistoryOpen, setIsHistoryOpen] = useState(false)
  const [history, setHistory] = useLocalStorage<SearchHistory>(
    'searchHistory',
    {}
  )

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value)
  }

  const handleHistoryItemClick = (id: string) => {
    return () => {
      setIsHistoryOpen(false)
      setQuery(id)
    }
  }

  const handleSelectAllModelTypes = (checked: boolean) => {
    setModelTypes({
      user: checked,
      employer: checked,
      employee: checked,
      member: checked,
      employerAdmin: checked,
      partner: checked,
      partnerAdmin: checked,
      prospect: checked,
    })
  }

  // Keep the URL in sync with the state.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (query) {
      params.set('query', query)
    } else {
      params.delete('query')
    }
    Object.entries(modelTypes).forEach(([key, value]) => {
      if (value) {
        params.set(key, value.toString())
      } else {
        params.delete(key)
      }
    })
    window.history.pushState(
      {},
      '',
      `${window.location.pathname}?${params.toString()}`
    )
  }, [query, modelTypes])

  // Keep state in sync when the user navigates browser history (back/forward).
  useEffect(() => {
    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search)
      setQuery(params.get('query') || '') // Update state based on the URL.
      setModelTypes({
        user: params.get('user') === 'true' || false,
        employer: params.get('employer') === 'true' || false,
        employee: params.get('employee') === 'true' || false,
        member: params.get('member') === 'true' || false,
        employerAdmin: params.get('employerAdmin') === 'true' || false,
        partner: params.get('partner') === 'true' || false,
        partnerAdmin: params.get('partnerAdmin') === 'true' || false,
        prospect: params.get('prospect') === 'true' || false,
      })
    }

    window.addEventListener('popstate', handlePopState)

    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [])

  // Whenever a new search result is received and it includes the persisted flag, add it to the search history.
  // Only searched by ID will be saved to avoid saving a bunch of partial string searches.
  useEffect(() => {
    if (data && data[0]?.persisted) {
      const result = data[0]
      const { id } = result
      setHistory((prev) => {
        const updatedHistory = {
          ...prev,
          [id]: {
            id,
            identifier: getIdentifierFromSearchResult(result),
            createdAt: new Date(),
          },
        }

        // Clear out old search results if there's > 20 results.
        if (Object.keys(updatedHistory).length > 20) {
          const sorted = Object.values(updatedHistory).sort(sortByCreatedAt)
          const toRemove = sorted.slice(20)
          toRemove.forEach((item) => {
            delete updatedHistory[item.id]
          })
        }

        return updatedHistory
      })
    }
  }, [data])

  function handleCopyURL () {
    navigator.clipboard.writeText(window.location.href)
    toast.success('URL copied to clipboard', { position: 'top-center' })
  }

  return (
    <SearchContext.Provider value={{ setQuery, setIsHistoryOpen }}>
      <div
        className='bg-white rounded-md p-4'
        style={{ minHeight: '85vh' }}
      >
        <div className='flex items-center justify-between pb-4'>
          <h1 className='h1'>Thatch Search</h1>
          <div>
            <Button
              variant='destructive'
              className='mr-2'
              onClick={() => setQuery('')}
            >
              Clear
            </Button>
            <Button
              variant='secondary'
              className='mr-2'
              onClick={handleCopyURL}
            >
              Copy URL
            </Button>
            <Button onClick={() => setIsHistoryOpen(true)}>History</Button>
          </div>
        </div>

        <div className='my-2 p-2 border rounded-md'>
          <div>Select the types of models to search for:</div>
          <div className='flex items-center text-sm flex-wrap mt-2'>
            {Object.keys(modelTypes).map((modelType) => (
              <div key={modelType} className='mr-2 flex items-center'>
                <Checkbox
                  checked={modelTypes[modelType as keyof ModelTypesState]}
                  onCheckedChange={(isChecked) => {
                    setModelTypes((prev) => ({
                      ...prev,
                      [modelType]: isChecked,
                    }))
                  }}
                />
                <label className='ml-2'>{modelTypeFromType(modelType)}</label>
              </div>
            ))}
            <div className='flex items-center'>
              <Checkbox
                checked={Object.values(modelTypes).every((value) => !!value)}
                onCheckedChange={handleSelectAllModelTypes}
              />
              <label className='ml-2 flex items-center'>
                Select all
                <span className='ml-1 flex items-center text-red-500'>
                  <SelectAllTooltip>
                    <TriangleAlert size={16} />
                  </SelectAllTooltip>
                </span>
              </label>
            </div>
          </div>
          <div className='mt-1 text-ds-text-secondary text-xs'>
            ***Search by ID is always enabled
          </div>
        </div>

        <div className='pb-2 italic'>
          Search by name, email or Thatch ID. Related results will be indented
          below key results.
        </div>
        <div>
          <Input
            type='text'
            placeholder='Search by ID, name or email'
            value={query}
            onChange={handleChange}
          />
        </div>

        <SearchResults isLoading={isLoading} data={data} error={error} />

        <Sheet open={isHistoryOpen} onOpenChange={setIsHistoryOpen}>
          <SheetContent className='h-dvh overflow-y-auto'>
            <SheetHeader>
              <SheetTitle className='flex items-center justify-between pb-10'>
                <div className='heading-lg'>Search History</div>
                <Button
                  variant='destructive'
                  className='mr-2'
                  onClick={() => setHistory({})}
                >
                  Clear
                </Button>
              </SheetTitle>
            </SheetHeader>
            <SheetDescription className='mb-4'>
              Click on a result below to display search results
            </SheetDescription>
            {Object.values(history)
              .sort(sortByCreatedAt)
              .map((item) => (
                <HistoryItem
                  key={item.id}
                  item={item}
                  handleClick={handleHistoryItemClick(item.id)}
                />
              ))}
          </SheetContent>
        </Sheet>
      </div>
    </SearchContext.Provider>
  )
}

const SearchResults = ({
  isLoading,
  data,
  error,
}: {
  isLoading: boolean
  data: SearchResult[] | undefined
  error: any
}) => {
  if (error) {
    return (
      <div className='text-red-500'>
        <div>There was an error processing your search. Please try again</div>
        <div>{error.message}</div>
      </div>
    )
  }

  return (
    <div className='pt-6'>
      {isLoading
        ? (
          <div className='flex justify-center'>
            <ThreeDots
              stroke='#4f46e5'
              fill='#4f46e5'
              style={{ width: '40px', height: '16px' }}
            />
          </div>
          )
        : (
          <>
            {data?.map((result) => (
              <Result key={result.id} result={result} />
            ))}
          </>
          )}
    </div>
  )
}

const Result = ({
  result,
  isRelated = false,
}: {
  result: SearchResult
  isRelated?: boolean
}) => {
  return (
    <>
      <div
        className={clsx({
          'p-2 border rounded-md mb-2 shadow-sm': true,
          'border-thatch-purple': !isRelated,
        })}
      >
        <div className='flex flex-col'>
          <div className='flex'>
            <Type type={result.type} />
            <ThatchID id={result.id} />
          </div>
          <Metadata result={result} />
        </div>
      </div>
      <div className='ml-8'>
        {result.related?.map((related) => (
          <Result key={related.id} result={related} isRelated />
        ))}
      </div>
    </>
  )
}

const Type = ({ type }: { type: string }) => {
  const classes = getTypeClasses(type)

  return (
    <span
      className={clsx({
        'inline-flex items-center rounded-[8px] px-2 py-1 text-xs leading-[18px] font-semibold':
          true,
        [classes]: true,
      })}
    >
      {type}
    </span>
  )
}

// getTypeClasses uses a result's type to determine its styling.
function getTypeClasses (type: string) {
  switch (type) {
    case 'Employee':
      return 'bg-green-100 text-green-800'
    case 'Employer':
      return 'bg-blue-100 text-blue-800'
    case 'Member':
      return 'bg-thatch-purple-active text-white'
    case 'EmployerAdmin':
      return 'bg-red-100 text-red-800'
    case 'User':
      return 'bg-cyan-100 text-cyan-800'
    case 'Partner':
      return 'bg-blue-100 text-blue-800'
    case 'PartnerAdmin':
      return 'bg-red-100 text-red-800'
    case 'Quoting::Prospect':
      return 'bg-purple-100 text-purple-800'
    case 'EmployerInvoice':
      return 'bg-yellow-100 text-yellow-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

// ThatchID displays a Thatch ID with a link to the ID in inspect, a copy button and a search icon to save the search result.
const ThatchID = ({ id }: { id: string }) => {
  const { setQuery } = useContext(SearchContext)

  return (
    <div className='flex items-center ml-2'>
      <span>{id}</span>
      <SquareArrowOutUpRight
        className='cursor-pointer ml-2'
        size={16}
        onClick={() => window.open(`inspect/${id}`)}
      />
      <CopyButtonWithAnimation content={id}>
        <Copy size={16} />
      </CopyButtonWithAnimation>
      <Search
        className='cursor-pointer ml-2'
        size={16}
        onClick={() => setQuery(id)}
      />
    </div>
  )
}

type EmployerMetadata = {
  name: string
  dba: string
}

type EmployeeMetaData = {
  first_name: string
  last_name: string
  personal_email: string
  work_email: string
}

type MemberMetadata = {
  given_name: string
  family_name: string
}

type UserMetadata = {
  email: string
  phone_number: string
}

type PartnerMetadata = {
  email: string
  phone_number: string
  display_name: string
}

type PartnerAdminMetadata = {
  email: string
  phone_number: string
  first_name: string
  last_name: string
}

type ProspectMetadata = {
  name: string
  email: string
  phone_number: string
}

type EmployerInvoiceMetadata = {
  status: string
  period_start_date: string
  period_end_date: string
}

type EmployerAdminMetadata = UserMetadata & {
  user_id: string
}

// BadgeWithLinkAndCopy displays the metadata of a search result with a link to a related page and a copy button.
const BadgeWithLinkAndCopy = ({
  text,
  link,
  slackText,
}: {
  text: string
  link: string
  slackText?: string
}) => {
  const encodedURI = encodeURI(`${window.location.origin}${link}`)

  return (
    <Button
      variant='outline'
      onClick={() => window.open(`${window.location.origin}${link}`)}
    >
      <div>{text}</div>
      <CopyButtonWithAnimation content={`${window.location.origin}${link}`}>
        <Copy size={16} />
      </CopyButtonWithAnimation>
      {slackText && (
        <CopyButtonWithAnimation content={`[${slackText}](${encodedURI})`}>
          <Slack size={16} />
        </CopyButtonWithAnimation>
      )}
    </Button>
  )
}

const MetadataInfo = ({ children }: { children: React.ReactNode }) => (
  <div className='text-ds-text-secondary mt-2'>{children}</div>
)

const MetadataLinks = ({ children }: { children: React.ReactNode }) => (
  <div className='flex items-center mt-2 space-x-2'>{children}</div>
)

// Metadata displays the metadata of a search result with helpful links and copy buttons to
// often critical information based on the result type.
const Metadata = ({ result }: { result: SearchResult }) => {
  const { id, type, metadata } = result

  if (!metadata) return undefined
  let parsed = JSON.parse(metadata)

  switch (type) {
    case 'Employee':
      parsed = parsed as EmployeeMetaData
      return (
        <>
          {parsed && (
            <MetadataInfo>
              {parsed.first_name} {parsed.last_name}{' '}
              {parsed.personal_email && (
                <span>personal: {parsed.personal_email}</span>
              )}
              {parsed.work_email && <span>work: {parsed.work_email}</span>}
            </MetadataInfo>
          )}
          <MetadataLinks>
            <BadgeWithLinkAndCopy
              text='Edit Employee'
              link={adminEmployees.edit({ id })}
            />
          </MetadataLinks>
        </>
      )
    case 'Employer':
      parsed = parsed as EmployerMetadata
      return (
        <>
          {parsed && (
            <MetadataInfo>
              {parsed.name} {parsed.dba ? `(dba: ${parsed.dba})` : ''}
            </MetadataInfo>
          )}
          <MetadataLinks>
            <BadgeWithLinkAndCopy
              text='Admin'
              slackText={`Admin: ${parsed.name} ${
                parsed.dba ? `(dba: ${parsed.dba})` : ''
              }`}
              link={adminEmployers.show({ id })}
            />
            <BadgeWithLinkAndCopy
              text='Broker'
              slackText={`MBP: ${parsed.name} ${
                parsed.dba ? `(dba: ${parsed.dba})` : ''
              }`}
              link={brokersEmployees.index({ employer_id: id })}
            />
          </MetadataLinks>
        </>
      )
    case 'Member':
      parsed = parsed as MemberMetadata
      return (
        <>
          {parsed && (
            <MetadataInfo>
              {parsed.given_name} {parsed.family_name}
            </MetadataInfo>
          )}
          <MetadataLinks>
            <BadgeWithLinkAndCopy
              text='Admin'
              slackText={`Admin: ${parsed.given_name} ${parsed.family_name}`}
              link={adminMembers.show({ id })}
            />
            <BadgeWithLinkAndCopy
              text='Broker'
              slackText={`MBP: ${parsed.given_name} ${parsed.family_name}`}
              link={brokersMembers.show({ id })}
            />
          </MetadataLinks>
        </>
      )
    case 'EmployerAdmin':
      parsed = parsed as EmployerAdminMetadata
      return (
        <>
          <MetadataInfo>{parsed.email}</MetadataInfo>
          <MetadataLinks>
            <BadgeWithLinkAndCopy
              text='User'
              slackText={`User: ${parsed.email}`}
              link={adminUsers.show({ id: parsed.user_id })}
            />
            <BadgeWithLinkAndCopy
              text='Edit User'
              link={adminUsers.edit({ id: parsed.user_id })}
            />
          </MetadataLinks>
        </>
      )
    case 'User':
      parsed = parsed as UserMetadata
      return (
        <>
          <MetadataInfo>{parsed.email}</MetadataInfo>
          <MetadataLinks>
            <BadgeWithLinkAndCopy
              text='User'
              slackText={`User: ${parsed.email}`}
              link={adminUsers.show({ id })}
            />
            <BadgeWithLinkAndCopy
              text='Edit User'
              link={adminUsers.edit({ id })}
            />
          </MetadataLinks>
        </>
      )
    case 'Partner':
      parsed = parsed as PartnerMetadata
      return (
        <>
          <MetadataInfo>{parsed.display_name}</MetadataInfo>
          <MetadataLinks>
            <BadgeWithLinkAndCopy
              text='Partner'
              slackText={`Partner: ${parsed.email}`}
              link={adminPartners.show({ id })}
            />
          </MetadataLinks>
        </>
      )
    case 'PartnerAdmin':
      parsed = parsed as PartnerAdminMetadata
      return (
        <>
          <MetadataInfo>
            {parsed.first_name} {parsed.last_name}
          </MetadataInfo>
        </>
      )
    case 'Quoting::Prospect':
      parsed = parsed as ProspectMetadata
      return (
        <>
          <MetadataInfo>
            {parsed.name}
          </MetadataInfo>
          <MetadataLinks>
            <BadgeWithLinkAndCopy
              text='Prospect'
              slackText={`Prospect: ${parsed.name}`}
              link={adminProspects.show({ id })}
            />
          </MetadataLinks>
        </>
      )
    case 'EmployerInvoice':
      parsed = parsed as EmployerInvoiceMetadata
      return (
        <>
          <MetadataInfo>
            {new Date(parsed.period_start_date).toLocaleDateString()} - {new Date(parsed.period_end_date).toLocaleDateString()} | {parsed.status}
          </MetadataInfo>
          <MetadataLinks>
            <BadgeWithLinkAndCopy
              text='Admin'
              slackText={`Invoice: ${id}`}
              link={adminEmployerInvoices.show({ id })}
            />
          </MetadataLinks>
        </>
      )
    default:
      return null
  }
}

// HistoryItem displays a search result history item with an ID and an identifier that can be
// clicked to bring back the result.
const HistoryItem = ({
  item,
  handleClick,
}: {
  item: SearchResultHistory
  handleClick: () => void
}) => {
  const [isHovered, setIsHovered] = useState(false)

  return (
    <div
      className={clsx({
        'p-2 border rounded-md mb-2 shadow-sm cursor-pointer text-sm': true,
        'border-thatch-purple': isHovered,
        'border-gray-200': !isHovered,
      })}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleClick}
    >
      <div>{item.id}</div>
      {item.identifier && (
        <div className='text-ds-thatch-secondary'>{item.identifier}</div>
      )}
    </div>
  )
}

// getIdentifierFromSearchResult returns a human-readable identifier from a search result.
function getIdentifierFromSearchResult ({
  metadata,
  type,
}: SearchResult): string | undefined {
  if (!metadata) return undefined

  let parsed = JSON.parse(metadata)

  switch (type) {
    case 'Employee':
      parsed = parsed as EmployeeMetaData
      return `${parsed.first_name} ${parsed.last_name}`
    case 'Employer':
      parsed = parsed as EmployerMetadata
      return `${parsed.name} ${parsed.dba ? `(dba: ${parsed.dba})` : ''}`
    case 'Member':
      parsed = parsed as MemberMetadata
      return `${parsed.given_name} ${parsed.family_name}`
    case 'EmployerAdmin':
      parsed = parsed as EmployerAdminMetadata
      return parsed.email
    case 'User':
      parsed = parsed as UserMetadata
      return parsed.email
    default:
      return undefined
  }
}

const ThatchSearch = () => {
  return (
    <ThatchSearchImpl />
  )
}

export default ThatchSearch
